import { Alert, Switch } from "@mui/material";
import { SwitchComponent } from "@syncfusion/ej2-react-buttons"
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons/check-box';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars/datetimepicker';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns/drop-down-list';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns/multi-select';
import React, { Component, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux';
import { isClubOwner, isClubStaff, isUser } from "../../util/UserRoles";
import { clubShowsReservationPayment, defaultReservationIsFree } from "../../util/calendarAccess";
import { UserAPI } from "../../api/UserAPI";
import { CLUB_PLAYER_SEARCH_DEBOUNCE_MS, canRemoveReservationPlayer, canSearchClubPlayers, clubUsersToPlayerOptions, hideReservationEditorAlert, mergePlayerOptions, reservationPlayerOverCapMessage, reservationPlayerSelectionCap, searchReservationPlayers } from "../../util/reservationPlayerSearch";

const EditorTemplateDesign = ({ args, players, courtNo, userId, chargesCourtTime = false }) => {
    const { calenderConfig } = useSelector((store) => store.calendar);
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const [toggle, setToggle] = useState(args?.IsAllDay);
    const [playerData, setPlayerData] = useState([]);
    const [playerOptions, setPlayerOptions] = useState([]);
    const playerOptionsRef = useRef([]);
    const subscriberListRef = useRef(null);
    const searchTimer = useRef(null);
    const [isFree, setIsFree] = useState(defaultReservationIsFree(selectedClub, args?.IsFree, chargesCourtTime));
    const [maxPlayerWarning, setMaxPlayerWarning] = useState('');
    const handleToggle = () => {
        setToggle(!toggle)
    }

    const onRemoving = (event) => {
        const removedId = event?.itemData?.id
            ?? event?.itemData?.userId
            ?? event?.item?.getAttribute?.("data-value");
        if (!canRemoveReservationPlayer({
            lockSelf: isUser(userInfo),
            userId,
            removedId,
        })) {
            event.cancel = true;
        }
    }
    const onPlayerChange = (event) => {
        const next = Array.isArray(event?.value) ? event.value : [];
        setPlayerData(next);
        if (next.length > 0) {
            hideReservationEditorAlert(document.getElementById("reservation-editor-root"));
            setMaxPlayerWarning("");
        }
    }
    const staffBooking = isClubOwner(userInfo) || isClubStaff(userInfo);
    const playerBooking = isUser(userInfo);
    const playerSelectionCap = reservationPlayerSelectionCap({
        staff: staffBooking,
        player: playerBooking,
    });
    const onSelect = (e) => {
        const currentPlayers = document.querySelector('#Player')?.ej2_instances?.[0]?.value || [];
        const warning = reservationPlayerOverCapMessage({
            staff: staffBooking,
            player: playerBooking,
            selectedCount: Array.isArray(currentPlayers) ? currentPlayers.length : 0,
            includingPendingSelect: true,
        });
        if (warning) {
            setMaxPlayerWarning(warning);
            e.cancel = true;
            return;
        }
        setMaxPlayerWarning('');
    }

    useEffect(() => {
        const selected = clubUsersToPlayerOptions(args?.PlayerList || players);
        if (args.Player) {
            setPlayerData(args.Player);
            setPlayerOptions(selected);
        } else if (isUser(userInfo)) {
            setPlayerData(userId ? [userId] : []);
            setPlayerOptions(mergePlayerOptions(selected, userId ? [{
                id: userId,
                name: [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(" ").trim() || "You",
            }] : []));
        } else {
            setPlayerData([]);
            setPlayerOptions(selected);
        }
        if (args?.IsFree !== undefined) {
            setIsFree(args.IsFree);
        }
    }, [args?.Id, userId])

    useEffect(() => {
        playerOptionsRef.current = playerOptions;
    }, [playerOptions]);

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    const onFiltering = (event) => {
        event.preventDefaultAction = true;
        const query = String(event.text || "").trim();
        if (!canSearchClubPlayers(query) || !selectedClub?.clubId) {
            event.updateData(playerOptionsRef.current);
            return;
        }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(async () => {
            try {
                const found = await searchReservationPlayers({
                    club: selectedClub,
                    query,
                    searchClubUsers: async (clubId, name) => {
                        const response = await UserAPI.getAllClubUserByName(clubId, name);
                        return response?.Data;
                    },
                    loadSubscribers: async (clubId) => {
                        if (!subscriberListRef.current) {
                            const response = await UserAPI.getAllUsersForAClub(clubId);
                            subscriberListRef.current = response?.Data || [];
                        }
                        return subscriberListRef.current;
                    },
                });
                const next = mergePlayerOptions(playerOptionsRef.current, found);
                setPlayerOptions(next);
                event.updateData(next);
            } catch (error) {
                event.updateData(playerOptionsRef.current);
            }
        }, CLUB_PLAYER_SEARCH_DEBOUNCE_MS);
    }


    const fieldsForPlayers = { text: 'name', value: 'id' };
    const fieldsForCourt = { text: 'courtNumber', value: 'id' };

    return (
        <div id="reservation-editor-root">
           <p id="reservation-form-alert" data-testid="reservation-form-alert" className="reservation-form-alert" hidden role="alert"></p>
           {maxPlayerWarning !== '' &&
           <Alert sx={{width:'88%'}} variant="outlined" severity="error" >
                {maxPlayerWarning}
            </Alert>}
            <table className='custom-event-editor' width={{ width: '100%' }}>
                <tbody>
                    <tr>
                        <td data-testid="court-number-label" className='e-textlabel'>Court Name</td>
                        <td>
                            <DropDownListComponent className='e-field' id='CourtNumber' fields={fieldsForCourt} dataSource={courtNo} placeholder='choose court no' data-name='CourtNumber' value={args?.CourtNumber || null}></DropDownListComponent>
                        </td>
                    </tr>
                    <tr>
                        <td data-testid="players-name-label" className='e-textlabel'>Players Name</td>


                        <td>
                            <MultiSelectComponent
                                className='e-field'
                                id='Player'
                                dataSource={playerOptions}
                                fields={fieldsForPlayers}
                                placeholder='Type at least 3 letters'
                                data-name='Player'
                                mode='Box'
                                select={onSelect}
                                allowFiltering={true}
                                filterType='Contains'
                                filtering={onFiltering}
                                value={playerData}
                                change={onPlayerChange}
                                removing={onRemoving}
                                {...(playerSelectionCap != null ? { maximumSelectionLength: playerSelectionCap } : {})}
                            >
                            </MultiSelectComponent>
                        </td>
                    </tr>
                    <tr style={{ visibility: toggle ? 'hidden' : 'visible' }}>
                        <td data-testid="dateTimePickerFromLabel" className='e-textlabel'>From</td>
                        <td>
                            <DateTimePickerComponent data-testid="dateTimePickerFrom" className='e-field' id='StartTime' data-name='StartTime' step={Number(calenderConfig?.interval)} value={args?.StartTime || args?.StartTime}>
                            </DateTimePickerComponent>
                        </td>
                    </tr>
                    <tr style={{ visibility: toggle ? 'hidden' : 'visible' }}>
                        <td data-testid="dateTimePickerToLabel" className='e-textlabel'>To</td>
                        <td>
                            <DateTimePickerComponent data-testid="dateTimePickerTo" className='e-field' id='EndTime' data-name='EndTime' step={Number(calenderConfig?.interval)} value={args?.EndTime || args?.EndTime}>
                            </DateTimePickerComponent>
                        </td>
                    </tr>
                    <tr>
                        <td data-testid="isallday-label" className='e-textlabel'>All Day</td>
                        <td>
                            <CheckBoxComponent data-testid="isallday" onClick={handleToggle} id='IsAllDay' className='e-field' data-name='IsAllDay' checked={args?.IsAllDay}>
                            </CheckBoxComponent>
                        </td>
                    </tr>

                    <tr>
                        <td data-testid="subject-container" style={{ visibility: 'hidden', position: 'absolute' }}>
                            <input type="text" data-testid="subject" name="Subject" id="Subject" defaultValue={'Reserved'} className='e-field e-input' />
                        </td>
                    </tr>
                    <tr>
                        <td data-testid="description-label" className='e-textlabel'>Description</td>
                        <td>
                            <textarea id='reservation_description' data-testid='description' className='e-field e-input' defaultValue={args?.Description} name='Description' rows={3} cols={50}>
                            </textarea>
                        </td>
                    </tr>

                </tbody>
            </table>
            <input
                type="checkbox"
                id="ReservationIsFreeBtn"
                checked={isFree}
                readOnly
                hidden
            />
            {staffBooking && (clubShowsReservationPayment(selectedClub) || chargesCourtTime) &&
                <div className="reservation-isfree-button-switch">
                    <p style={{ fontSize: '1rem' }}>Is this reservation free?</p>
                    <Switch onChange={(e) => setIsFree(e.target.checked)} checked={isFree} />
                </div>
            }
        </div>

    )
}

export default EditorTemplateDesign